"use client";

/**
 * Rendering dei 4 tipi di gallery specializzate (allineate al backend V15:
 * IMAGE_GALLERY, VIDEO_GALLERY, AUDIO_GALLERY, QUOTE_GALLERY).
 *
 * Questi componenti sono usati sia inline nel PageBuilder (blocchi
 * imageGallery/videoGallery/audioGallery/quoteGallery salvati nel JSON della
 * pagina) sia dal renderer di homeBlockRef (che converte un HomeBlockDto nel
 * corrispondente componente).
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import Image from "next/image";
import type {
  AudioGalleryBlockData,
  ImageGalleryBlockData,
  QuoteGalleryBlockData,
  VideoGalleryBlockData,
} from "@/components/admin/builder/types";
import { toYouTubeEmbed, youtubeThumbnail } from "@/lib/youtube";

/* ── Helpers di layout ──────────────────────────────────── */

function SectionHeader({
  title,
  subtitle,
  onDark,
}: {
  title?: string;
  subtitle?: string;
  onDark?: boolean;
}) {
  if (!title && !subtitle) return null;
  return (
    <header className="mb-6 text-center">
      {subtitle && (
        <div
          className={`text-xs font-bold uppercase tracking-[0.2em] mb-1 ${
            onDark ? "text-white/70" : "text-[#4a90c4]"
          }`}
        >
          {subtitle}
        </div>
      )}
      {title && (
        <h2
          className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${
            onDark ? "text-white" : "text-[#1a3a5c]"
          }`}
        >
          {title}
        </h2>
      )}
    </header>
  );
}

function gridCols(columns?: 2 | 3 | 4) {
  switch (columns) {
    case 2:
      return "sm:grid-cols-2";
    case 4:
      return "sm:grid-cols-2 lg:grid-cols-4";
    case 3:
    default:
      return "sm:grid-cols-2 lg:grid-cols-3";
  }
}

/* ── IMAGE GALLERY ──────────────────────────────────────── */

export function ImageGalleryBlock({
  title,
  subtitle,
  items,
  layout = "grid",
  columns = 3,
  bgColor,
  slideshowIntervalMs,
  slideshowAutoPlay,
  className,
}: ImageGalleryBlockData["props"]) {
  const visible = (items ?? []).filter((it) => !!it.url);
  const [current, setCurrent] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // Navigazione con tastiera nella lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i === null ? 0 : (i + 1) % visible.length));
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i === null ? 0 : (i - 1 + visible.length) % visible.length));
    };
    document.addEventListener("keydown", onKey);
    // Blocca scroll del body mentre la lightbox è aperta
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIdx, visible.length]);

  if (visible.length === 0) return null;
  const style: CSSProperties = bgColor ? { backgroundColor: bgColor } : {};
  const onDark = !!bgColor;

  const Tile = ({ item, index }: { item: typeof visible[number]; index: number }) => {
    const hasHref = !!item.href;
    // Se c'è un href esplicito, priorità all'href (apre link esterno).
    // Altrimenti qualsiasi click apre la lightbox.
    const Tag: React.ElementType = hasHref ? "a" : "button";
    // Lighthouse a11y: il button/<a> contiene solo un <Image> che spesso
    // ha alt="" (foto evento senza didascalia). Senza aria-label esplicita
    // gli screen reader leggono "button"/"link" senza contesto. Fallback
    // chain: alt → caption → "Foto {n}".
    const accessibleName =
      item.alt?.trim() ||
      item.caption?.trim() ||
      `${hasHref ? "Apri immagine" : "Apri lightbox"} ${index + 1}`;
    const linkProps = hasHref
      ? { href: item.href, target: "_blank", rel: "noopener noreferrer", "aria-label": accessibleName }
      : { type: "button" as const, onClick: () => setLightboxIdx(index), "aria-label": accessibleName };
    return (
      <figure className="w-full">
        <Tag
          {...linkProps}
          className="block w-full aspect-[4/3] rounded-xl overflow-hidden ring-1 ring-neutral-200 bg-neutral-100 hover:ring-[#4a90c4] transition-all group cursor-pointer relative"
        >
          {/* Tile gallery: aspect 4:3 fisso dal parent → fill funziona
              perfettamente. Sizes per grid responsive (2-3-4 col). */}
          <Image
            src={item.url}
            alt={item.alt || item.caption || ""}
            fill
            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            className="object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none"
          />
        </Tag>
        {item.caption && (
          <figcaption
            className={`mt-2 text-xs text-center ${onDark ? "text-white/70" : "text-neutral-500"}`}
          >
            {item.caption}
          </figcaption>
        )}
      </figure>
    );
  };

  return (
    <section
      className={`py-10 ${bgColor ? "w-full" : ""} ${className ?? ""}`}
      style={style}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader title={title} subtitle={subtitle} onDark={onDark} />
        {layout === "grid" && (
          <div className={`grid gap-4 ${gridCols(columns)}`}>
            {visible.map((item, i) => (
              <Tile key={i} item={item} index={i} />
            ))}
          </div>
        )}
        {layout === "strip" && (
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {visible.map((item, i) => (
              <div key={i} className="flex-shrink-0 w-[85%] sm:w-[45%] md:w-[30%] snap-start">
                <Tile item={item} index={i} />
              </div>
            ))}
          </div>
        )}
        {layout === "carousel" && (
          <CarouselWrapper
            count={visible.length}
            current={current}
            setCurrent={setCurrent}
            onDark={onDark}
          >
            <div className="max-w-3xl mx-auto">
              <Tile item={visible[current]} index={current} />
            </div>
          </CarouselWrapper>
        )}
        {layout === "slideshow" && (
          <SlideshowWrapper
            count={visible.length}
            current={current}
            setCurrent={setCurrent}
            onDark={onDark}
            intervalMs={slideshowIntervalMs ?? 4000}
            autoPlay={slideshowAutoPlay ?? true}
          >
            <div className="max-w-4xl mx-auto">
              <Tile item={visible[current]} index={current} />
            </div>
          </SlideshowWrapper>
        )}
      </div>

      {/* ── Lightbox ───────────────────────────────────────── */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
          role="dialog"
          aria-modal="true"
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Chiudi"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-4 z-10 text-white/80 text-sm tabular-nums bg-white/10 rounded-full px-3 py-1">
            {lightboxIdx + 1} / {visible.length}
          </div>

          {/* Prev */}
          {visible.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx((i) => (i === null ? 0 : (i - 1 + visible.length) % visible.length));
              }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Precedente"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Image lightbox */}
          <figure
            className="relative max-w-[95vw] max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Lightbox: l'immagine si apre full size, dim larghe (1920x1280
                = aspect 3:2 default) + h-auto/max-h-[85vh] limita display.
                sizes=95vw rispecchia il max-w del parent. */}
            <Image
              src={visible[lightboxIdx].url}
              alt={visible[lightboxIdx].alt || visible[lightboxIdx].caption || ""}
              width={1920}
              height={1280}
              sizes="95vw"
              className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
              priority
            />
            {visible[lightboxIdx].caption && (
              <figcaption className="mt-3 text-white/80 text-sm text-center max-w-2xl px-4">
                {visible[lightboxIdx].caption}
              </figcaption>
            )}
          </figure>

          {/* Next */}
          {visible.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx((i) => (i === null ? 0 : (i + 1) % visible.length));
              }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Successivo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/* ── VIDEO GALLERY ──────────────────────────────────────── */

function VideoTile({ item }: { item: { url: string; title?: string; caption?: string } }) {
  const [playing, setPlaying] = useState(false);
  const embed = toYouTubeEmbed(item.url);
  const thumb = embed ? youtubeThumbnail(item.url, "hq") : null;

  return (
    <figure className="w-full">
      {embed && !playing && thumb ? (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          // Lighthouse a11y: aria-label esplicita perche' il bottone
          // contiene solo immagine + icona SVG decorativa (no testo).
          aria-label={item.title ? `Riproduci video: ${item.title}` : "Riproduci video"}
          className="relative w-full aspect-video rounded-xl overflow-hidden bg-black group ring-1 ring-neutral-200 hover:ring-[#4a90c4] transition-all"
        >
          {/* YouTube thumbnail: aspect-video fissato dal parent → fill.
              Sizes adattato a grid 3-col tipica del video gallery. */}
          <Image
            src={thumb}
            alt={item.title || ""}
            fill
            sizes="(min-width: 768px) 33vw, 100vw"
            className="object-cover"
          />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-[#1a3a5c] ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </button>
      ) : embed ? (
        <div className="w-full aspect-video rounded-xl overflow-hidden ring-1 ring-neutral-200 bg-black">
          <iframe
            src={`${embed}?autoplay=1&rel=0&modestbranding=1`}
            title={item.title || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      ) : item.url ? (
        <div className="w-full aspect-video rounded-xl overflow-hidden ring-1 ring-neutral-200 bg-black">
          <video src={item.url} controls className="w-full h-full object-cover" />
        </div>
      ) : null}
      {(item.title || item.caption) && (
        <figcaption className="mt-2 text-center">
          {item.title && <div className="text-sm font-semibold text-neutral-800 line-clamp-1">{item.title}</div>}
          {item.caption && <div className="text-xs text-neutral-500 line-clamp-2">{item.caption}</div>}
        </figcaption>
      )}
    </figure>
  );
}

export function VideoGalleryBlock({
  title,
  subtitle,
  items,
  layout = "grid",
  columns = 2,
  bgColor,
  slideshowIntervalMs,
  slideshowAutoPlay,
  className,
}: VideoGalleryBlockData["props"]) {
  const visible = (items ?? []).filter((it) => !!it.url);
  const [current, setCurrent] = useState(0);
  if (visible.length === 0) return null;
  const style: CSSProperties = bgColor ? { backgroundColor: bgColor } : {};
  const onDark = !!bgColor;

  return (
    <section
      className={`py-10 ${bgColor ? "w-full" : ""} ${className ?? ""}`}
      style={style}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader title={title} subtitle={subtitle} onDark={onDark} />
        {layout === "grid" && (
          <div className={`grid gap-4 ${gridCols(columns)}`}>
            {visible.map((item, i) => (
              <VideoTile key={i} item={item} />
            ))}
          </div>
        )}
        {layout === "strip" && (
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {visible.map((item, i) => (
              <div key={i} className="flex-shrink-0 w-[90%] sm:w-[55%] md:w-[40%] snap-start">
                <VideoTile item={item} />
              </div>
            ))}
          </div>
        )}
        {layout === "carousel" && (
          <CarouselWrapper
            count={visible.length}
            current={current}
            setCurrent={setCurrent}
            onDark={onDark}
          >
            <div className="max-w-3xl mx-auto">
              <VideoTile item={visible[current]} />
            </div>
          </CarouselWrapper>
        )}
        {layout === "slideshow" && (
          <SlideshowWrapper
            count={visible.length}
            current={current}
            setCurrent={setCurrent}
            onDark={onDark}
            intervalMs={slideshowIntervalMs ?? 6000}
            autoPlay={slideshowAutoPlay ?? false}
          >
            <div className="max-w-4xl mx-auto">
              {/* key per forzare remount e fermare il video precedente */}
              <VideoTile key={current} item={visible[current]} />
            </div>
          </SlideshowWrapper>
        )}
      </div>
    </section>
  );
}

/* ── AUDIO GALLERY ──────────────────────────────────────── */

function AudioTile({ item }: { item: { url: string; title?: string; caption?: string } }) {
  const isSpotify = item.url.includes("spotify.com");
  if (isSpotify) {
    const embed = item.url.includes("/embed")
      ? item.url
      : item.url
          .replace("/episode/", "/embed/episode/")
          .replace("/show/", "/embed/show/")
          .replace("/track/", "/embed/track/");
    return (
      <figure>
        <div className="w-full rounded-xl overflow-hidden ring-1 ring-neutral-200">
          <iframe
            src={embed}
            width="100%"
            height="152"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title={item.title || "Podcast"}
          />
        </div>
        {(item.title || item.caption) && (
          <figcaption className="mt-2 text-center">
            {item.title && <div className="text-sm font-semibold text-neutral-800 line-clamp-1">{item.title}</div>}
            {item.caption && <div className="text-xs text-neutral-500 line-clamp-2">{item.caption}</div>}
          </figcaption>
        )}
      </figure>
    );
  }
  return (
    <figure className="w-full rounded-xl bg-gradient-to-br from-violet-50 to-sky-50 ring-1 ring-neutral-200 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
          <span className="text-2xl">🎙️</span>
        </div>
        <div className="min-w-0">
          <div className="font-bold text-sm text-neutral-800 truncate">{item.title || "Audio"}</div>
          {item.caption && <div className="text-xs text-neutral-500 truncate">{item.caption}</div>}
        </div>
      </div>
      <audio src={item.url} controls className="w-full h-10" />
    </figure>
  );
}

export function AudioGalleryBlock({
  title,
  subtitle,
  items,
  layout = "list",
  columns = 2,
  bgColor,
  className,
}: AudioGalleryBlockData["props"]) {
  const visible = (items ?? []).filter((it) => !!it.url);
  if (visible.length === 0) return null;
  const style: CSSProperties = bgColor ? { backgroundColor: bgColor } : {};
  const onDark = !!bgColor;

  return (
    <section
      className={`py-10 ${bgColor ? "w-full" : ""} ${className ?? ""}`}
      style={style}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <SectionHeader title={title} subtitle={subtitle} onDark={onDark} />
        {layout === "grid" ? (
          <div className={`grid gap-4 ${columns === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
            {visible.map((item, i) => (
              <AudioTile key={i} item={item} />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((item, i) => (
              <AudioTile key={i} item={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ── QUOTE GALLERY ──────────────────────────────────────── */

function QuoteCard({
  item,
  onDark,
}: {
  item: { text: string; author?: string; source?: string };
  onDark?: boolean;
}) {
  return (
    <blockquote
      className={`relative rounded-2xl px-6 py-7 sm:px-8 sm:py-9 border ${
        onDark
          ? "bg-white/5 border-white/10 text-white"
          : "bg-gradient-to-br from-[#f8f6f3] to-[#f0ece6] border-neutral-200/70 text-[#1a3a5c]"
      }`}
    >
      <svg
        className={`absolute top-4 left-4 w-8 h-8 ${onDark ? "text-white/10" : "text-[#4a90c4]/20"}`}
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
      </svg>
      <p className="relative text-lg sm:text-xl font-light italic leading-relaxed">
        &ldquo;{item.text}&rdquo;
      </p>
      {(item.author || item.source) && (
        <footer
          className={`mt-4 text-sm font-semibold tracking-wide ${
            onDark ? "text-white/70" : "text-[#4a90c4]"
          }`}
        >
          — {item.author}
          {item.author && item.source ? ", " : ""}
          {item.source && <span className="font-normal italic">{item.source}</span>}
        </footer>
      )}
    </blockquote>
  );
}

export function QuoteGalleryBlock({
  title,
  subtitle,
  items,
  layout = "grid",
  columns = 2,
  bgColor,
  slideshowIntervalMs,
  slideshowAutoPlay,
  className,
}: QuoteGalleryBlockData["props"]) {
  const visible = (items ?? []).filter((it) => !!it.text?.trim());
  const [current, setCurrent] = useState(0);
  if (visible.length === 0) return null;
  const style: CSSProperties = bgColor ? { backgroundColor: bgColor } : {};
  const onDark = !!bgColor;

  return (
    <section
      className={`py-10 ${bgColor ? "w-full" : ""} ${className ?? ""}`}
      style={style}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHeader title={title} subtitle={subtitle} onDark={onDark} />
        {layout === "grid" && (
          <div className={`grid gap-4 ${columns === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
            {visible.map((item, i) => (
              <QuoteCard key={i} item={item} onDark={onDark} />
            ))}
          </div>
        )}
        {layout === "single" && (
          <div className="max-w-3xl mx-auto">
            <QuoteCard item={visible[0]} onDark={onDark} />
          </div>
        )}
        {layout === "carousel" && (
          <CarouselWrapper
            count={visible.length}
            current={current}
            setCurrent={setCurrent}
            onDark={onDark}
          >
            <div className="max-w-3xl mx-auto">
              <QuoteCard item={visible[current]} onDark={onDark} />
            </div>
          </CarouselWrapper>
        )}
        {layout === "slideshow" && (
          <SlideshowWrapper
            count={visible.length}
            current={current}
            setCurrent={setCurrent}
            onDark={onDark}
            intervalMs={slideshowIntervalMs ?? 6000}
            autoPlay={slideshowAutoPlay ?? true}
          >
            <div className="max-w-3xl mx-auto">
              <QuoteCard item={visible[current]} onDark={onDark} />
            </div>
          </SlideshowWrapper>
        )}
      </div>
    </section>
  );
}

/* ── Carousel wrapper condiviso ─────────────────────────── */

function CarouselWrapper({
  count,
  current,
  setCurrent,
  onDark,
  children,
}: {
  count: number;
  current: number;
  setCurrent: (n: number) => void;
  onDark?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      {children}
      {count > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setCurrent((current - 1 + count) % count)}
            className={`p-2 rounded-full ${
              onDark
                ? "bg-white/10 hover:bg-white/20 text-white"
                : "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
            }`}
            aria-label="Precedente"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex gap-1.5">
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCurrent(i)}
                className={`h-2 rounded-full transition-all ${
                  i === current
                    ? "w-6 " + (onDark ? "bg-white" : "bg-[#4a90c4]")
                    : "w-2 " + (onDark ? "bg-white/30" : "bg-neutral-300")
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setCurrent((current + 1) % count)}
            className={`p-2 rounded-full ${
              onDark
                ? "bg-white/10 hover:bg-white/20 text-white"
                : "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
            }`}
            aria-label="Successivo"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Slideshow wrapper (autoplay + pausa hover + fade) ─── */

function SlideshowWrapper({
  count,
  current,
  setCurrent,
  onDark,
  intervalMs,
  autoPlay,
  children,
}: {
  count: number;
  current: number;
  setCurrent: (n: number) => void;
  onDark?: boolean;
  intervalMs: number;
  autoPlay: boolean;
  children: React.ReactNode;
}) {
  const [playing, setPlaying] = useState(autoPlay);
  const [paused, setPaused] = useState(false);
  const currentRef = useRef(current);
  currentRef.current = current;

  useEffect(() => {
    if (!playing || paused || count < 2) return;
    const id = window.setInterval(() => {
      setCurrent((currentRef.current + 1) % count);
    }, Math.max(1500, intervalMs));
    return () => window.clearInterval(id);
  }, [playing, paused, count, intervalMs, setCurrent]);

  const btnBase = onDark
    ? "bg-white/10 hover:bg-white/20 text-white"
    : "bg-neutral-100 hover:bg-neutral-200 text-neutral-700";

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slide corrente con transizione fade (key ottenuta via children già cambia) */}
      <div key={current} className="animate-[slideshow-fade_450ms_ease-out]">
        {children}
      </div>

      {count > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setCurrent((current - 1 + count) % count)}
            className={`p-2 rounded-full ${btnBase}`}
            aria-label="Precedente"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            className={`p-2 rounded-full ${btnBase}`}
            aria-label={playing ? "Pausa" : "Play"}
            title={playing ? "Pausa" : "Play"}
          >
            {playing ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <div className="flex gap-1.5">
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCurrent(i)}
                className={`h-2 rounded-full transition-all ${
                  i === current
                    ? "w-6 " + (onDark ? "bg-white" : "bg-[#4a90c4]")
                    : "w-2 " + (onDark ? "bg-white/30" : "bg-neutral-300")
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => setCurrent((current + 1) % count)}
            className={`p-2 rounded-full ${btnBase}`}
            aria-label="Successivo"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      <style jsx>{`
        @keyframes slideshow-fade {
          0%   { opacity: 0; transform: scale(0.98); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

